// @flow
import {observable, action, runInAction, makeObservable} from 'mobx';
import RootStore from 'mobx/RootStore';
import {
  UserResource,
  Collection,
  Resource,
} from '@wellstone-solutions/common/models/rest';
import type {ApiResponseType} from '@wellstone-solutions/common';
import type {
  UICollectionType,
  UIUserResourceType,
  UIResourceType,
} from '@wellstone-solutions/common/models/rest';
import {Events} from '@wellstone-solutions/common';

import type {IObservableArray, ObservableSet} from 'mobx';

// REST calls resolve 4xx responses with `isSuccess: false`, but 5xx, network
// errors and timeouts reject (see `validateStatus` in the common Api config).
// Fold rejections into the same shape so callers only branch on `isSuccess`.
const settle = async <T>(
  request: Promise<ApiResponseType<T>>,
): Promise<ApiResponseType<T>> => {
  console.log('Settling request:', request);
  try {
    return await request;
  } catch (error) {
    return {
      isSuccess: false,
      status: error?.status ?? 0,
      errors: error?.errors ?? [],
      // $FlowFixMe - callers replace `data` on failure
      data: null,
    };
  }
};

export class ResourceStoreV2 {
  rootStore: RootStore;
  collections: IObservableArray<UICollectionType> = observable.array();
  userResources: IObservableArray<UIUserResourceType> = observable.array();
  isLoadingUserResources: boolean = false;
  flaggedResourceIds: ObservableSet<string> = observable.set();

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      collections: observable,
      userResources: observable,
      getCollections: action,
      getUserResources: action,
      handleUserResource: action,
      isLoadingUserResources: observable,
      isSavedResource: action,
      updateResource: action,
      deleteResource: action,
      flaggedResourceIds: observable,
      markResourceFlagged: action,
    });

    this.rootStore = rootStore;
  }

  async getCollections(
    forceFetch: boolean = false,
  ): Promise<ApiResponseType<IObservableArray<UICollectionType> | []>> {
    if (!forceFetch && this.collections.length) {
      return Promise.resolve({
        isSuccess: true,
        errors: [],
        status: 200,
        data: this.collections,
      });
    }

    const response = await Collection.getAll();
    if (response.isSuccess) {
      const resourceCollections = response.data.filter(
        (collection) => collection.collection_type.name === 'Resources',
      );

      const uiCollections = resourceCollections.map(Collection.toUI);
      runInAction(() => {
        this.collections.replace(uiCollections);
      });

      return {...response, data: this.collections};
    } else {
      return {...response, data: []};
    }
  }

  async getUserResources(
    forceFetch?: boolean = false,
  ): Promise<ApiResponseType<IObservableArray<UIUserResourceType> | []>> {
    if (!forceFetch && this.userResources.length) {
      return Promise.resolve({
        isSuccess: true,
        errors: [],
        status: 200,
        data: this.userResources,
      });
    }

    runInAction(() => {
      this.isLoadingUserResources = true;
    });

    const response = await UserResource.getAll();
    if (response.isSuccess) {
      const uiUserResources = response.data.map(UserResource.toUI);
      runInAction(() => {
        this.userResources.replace(uiUserResources);
        this.isLoadingUserResources = false;
      });
      return {...response, data: this.userResources};
    } else {
      runInAction(() => {
        this.userResources.replace([]);
        this.isLoadingUserResources = false;
      });
      return {...response, data: []};
    }
  }

  async handleUserResource(
    resourceId: string,
  ): Promise<ApiResponseType<UIUserResourceType | void>> {
    const existingResource = await this.userResources.find(
      (ur) => ur.resource.id === resourceId,
    );
    let response;

    if (existingResource) {
      response = await UserResource.remove(existingResource.id);
      if (response.isSuccess) {
        runInAction(() => {
          this.userResources.remove(existingResource);
        });
      }
    } else {
      response = await UserResource.create({
        resourceId,
        // $FlowFixMe - meStore does not have types
        userId: this.rootStore.stores.meStore.me.id,
      });

      if (response.isSuccess) {
        const uiUserResource = UserResource.toUI(response.data);
        runInAction(() => {
          this.userResources.push(uiUserResource);
        });

        this.rootStore.stores.eventStore.addEvent(Events.USER_SAVED_RESOURCE, {
          id: resourceId,
        });
      }
    }
    return response;
  }

  isSavedResource(resourceId: string): boolean {
    return this.userResources.some((ur) => ur.resource.id === resourceId);
  }

  isResourceFlagged(resource: UIResourceType): boolean {
    return (
      Boolean(resource.hasActiveFlag) ||
      this.flaggedResourceIds.has(resource.id)
    );
  }

  markResourceFlagged(resourceId: string): void {
    this.flaggedResourceIds.add(resourceId);
  }

  // Admin create/edit/delete for the shared resource database, via the same
  // REST `Resource` model Navigator's resource admin uses.
  async getResource(
    id: string,
  ): Promise<ApiResponseType<UIResourceType | null>> {
    const response = await settle(Resource.get(id));

    if (!response.isSuccess) {
      return {...response, data: null};
    }

    return {...response, data: Resource.toUI(response.data)};
  }

  async createResource(
    resource: UIResourceType,
  ): Promise<ApiResponseType<UIResourceType | null>> {
    const response = await settle(
      Resource.create(Resource.toApiInput(resource)),
    );

    if (!response.isSuccess) {
      return {...response, data: null};
    }

    return {...response, data: Resource.toUI(response.data)};
  }

  async updateResource(
    resource: UIResourceType,
  ): Promise<ApiResponseType<UIResourceType | null>> {
    const response = await settle(
      Resource.update(resource.id, Resource.toApiInput(resource)),
    );

    if (!response.isSuccess) {
      return {...response, data: null};
    }

    const updated = Resource.toUI(response.data);

    // Saving resolves any active flag on the backend.
    // Saved resources embed their own copy of the resource.
    runInAction(() => {
      this.flaggedResourceIds.delete(updated.id);
      this.userResources.replace(
        this.userResources.map((ur) =>
          ur.resource.id === updated.id ? {...ur, resource: updated} : ur,
        ),
      );
    });

    return {...response, data: updated};
  }

  async deleteResource(id: string): Promise<ApiResponseType<boolean>> {
    const response = await settle(Resource.remove(id));

    if (!response.isSuccess) {
      return {...response, data: false};
    }

    runInAction(() => {
      this.userResources.replace(
        this.userResources.filter((ur) => ur.resource.id !== id),
      );
    });

    return response;
  }
}
